import { FC, useEffect, useMemo, useState } from "react";
import billingDataRouteRequestsFactory from "./factory/billing-data-route-requests.factory";
import billingMapRoutesFactory from "./factory/billing-map-routes.factory";
import BillingsNode from "../../common/types/billings-node";
import billingRecalculateRequestFactory from "./factory/billing-recalculate-request.factory";
import billingDataFactory from "./factory/billing-data.factory";
import BillingDetailsComponent from "./billing-details/billing-details.component";
import ContractDetailsComponent from "./contract-details/contract-details.component";
import billingSaveRequestFactory from "./factory/billing-save-request.factory";
import ButtonComponent from "../../../../common/components/button/button.component";
import CardComponent from "../../../../common/components/card/card.component";
import Column from "../../../../common/components/grid/column";
import Row from "../../../../common/components/grid/row";
import MapComponent from "../../../../common/components/map/map.component";
import MapMarker from "../../../../common/components/map/types/map-marker";
import MapRoute from "../../../../common/components/map/types/map-route";
import SearchRoadRoutingResponse from "../../../../common/utils/search-road-route/search-road-routing.response";
import BillingFormData, { BillingSummaryData } from "./types/billing-form.data";
import { useNavigate, useParams } from "react-router-dom";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import formValidationService from "../../../../common/utils/validation/form-validation.service";
import notificationService from "../../../../common/utils/notification/notification.service";
import billingRoutesHelper from "../../common/routes/billing-routes.helper";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import BillingTaxiTaxiAddRouteParams from "../../common/routes/types/billing-taxi-taxi-add-route-params";
import BillingsSummaryConfirmationComponent from "./billings-summary-confirmation.component";
import RelatedBillingsCargoTaxiComponent from "../common/related-billings/cargo-taxi/related-billings-cargo-taxi.component";
import RelatedBillingsTaxiDriverComponent from "../common/related-billings/taxi-driver/related-billings-taxi-driver.component";
import RelatedBillingsTaxiTaxiComponent from "../common/related-billings/taxi-taxi/related-billings-taxi-taxi.component";
import BillingRouteDetailsFormDataValidationResult, {
  NodeValidationResult,
} from "../../common/route-details/types/billings-route-details-form-data-validation-result";
import billingRouteDetailsHelper from "../../common/route-details/common/billings-route-details.helper";
import billingRouteDetailsDataValidationService from "../../common/route-details/common/billing-route-details-form-data-validation.service";
import BillingsRouteDetailsFormComponent from "../../common/route-details/billings-route-details-form.component";
import MessengerComponent from "../../../../common/components/messenger/messenger.component";
import billingsTaxiTaxiHelper from "./billings-taxi-taxi.helper";
import billingsMapMarkersFactory from "../../common/map-markers/billings-map-markers.factory";
import BillingContributionsValidationResult from "./types/billing-contributions-validation-result";
import BillingsTaxiTaxiContributionBonus, {
  BillingsTaxiTaxiContributionBonusFavorableDistance,
  BillingsTaxiTaxiContributionBonusOther,
  BillingsTaxiTaxiContributionBonusType,
} from "../common/contributions/bonus/types/billings-taxi-taxi-contributions-bonus";
import BillingsTaxiTaxiContributionPenalty, {
  BillingsTaxiTaxiContributionPenaltyType,
} from "../common/contributions/penalty/types/billings-taxi-taxi-contributions-penalty";
import BillingContributionType from "./types/billing-contribution-type";
import billingsTaxiTaxiContributionsValidationService from "../common/contributions/billings-taxi-taxi-contributions-validation.service";
import TabsData from "../../../../common/components/tabs/common/types/tabs-data";
import BillingsToolsComponent from "../../common/tools/billings-tools.component";
import BillingRouteDetailsComponent from "./billing-route-details/billing-route-details.component";
import TabsComponent from "../../../../common/components/tabs/tabs.component";
import useApiCall from "../../common/hooks/use-api-call";
import OrderDetailsHistoryComponent from "../../../order/details/history/order-details-history.component";
import BillingModel from "../../types/billing-model";
import useAbort from "../../../../common/hooks/use-abort";
import searchRoadRoutingService from "../../../../common/utils/search-road-route/search-road-routing.service";
import billingService from "../../../../common/services/billings/billings.service";
import useTaxiTaxiBillingDraft from "../../../../common/services/billings/order-forward/taxi-taxi/draft/use-billings-order-forward-taxi-taxi-draft";
import BillingsOrderForwardTaxiTaxiDraftParams from "../../../../common/services/billings/order-forward/taxi-taxi/draft/billings-order-forward-taxi-taxi-draft-params";
import BillingsOrderForwardTaxiTaxiDraft from "../../../../common/services/billings/order-forward/taxi-taxi/draft/billings-order-forward-taxi-taxi-draft";
import useGpsPlanEntryRecords from "../../../../common/services/gps/plan-entry-records/use-gps-plan-entry-records";
import GpsPlanEntryRecordsParams from "../../../../common/services/gps/plan-entry-records/gps-plan-entry-records-params";
import GpsPlanEntryRecords from "../../../../common/services/gps/plan-entry-records/gps-plan-entry-records";
import BillingsOrderForwardTaxiTaxiRecalculate from "../../../../common/services/billings/order-forward/taxi-taxi/recalculate/billings-order-forward-taxi-taxi-recalculate";
import useBillingsOrderForwardTaxiTaxiRecalculate from "../../../../common/services/billings/order-forward/taxi-taxi/recalculate/use-billings-order-forward-taxi-taxi-details";

type BillingsTaxiTaxiAddProps = {};

const BillingsTaxiTaxiAddComponent: FC<BillingsTaxiTaxiAddProps> = () => {
  const { forwardingUuid } = useParams<BillingTaxiTaxiAddRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [isBillingSavePending, setIsBillingSavePending] = useState(false);

  const [routeId, setRouteId] = useState("");

  const [billingData, setBillingData] = useState<BillingFormData>();
  const [billingFormData, setBillingFormData] = useState<BillingFormData>();
  const [billingSummaryData, setBillingSummaryData] =
    useState<BillingSummaryData>();

  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>();
  const [plannedMapRoute, setPlannedMapRoute] = useState<MapRoute | null>(null);
  const [completedMapRoute, setCompletedMapRoute] = useState<MapRoute | null>(
    null,
  );
  const [isSummaryConfirmationVisible, setIsSummaryConfirmationVisible] =
    useState(false);
  const [shouldRetrieveRouteData, setShouldRetrieveRouteData] = useState(false);

  const [isBillingDataLoading, setIsBillingDataLoading] = useState(false);

  const routeCompletedRoute = useGpsPlanEntryRecords();
  const routeCompletedRouteAbort = useAbort();

  const addTaxiTaxiBillingDataAbort = useAbort();

  const recalculateTaxiTaxiBilling =
    useBillingsOrderForwardTaxiTaxiRecalculate();
  const recalculateTaxiTaxiBillingAbort = useAbort();

  const taxiTaxiBillingDraft = useTaxiTaxiBillingDraft();
  const taxiTaxiBillingDraftAbort = useAbort();

  const [formValidationResults, setFormValidationResults] =
    useState<BillingRouteDetailsFormDataValidationResult>({
      discountValue: formValidationService.defaultValidationResult,
      nodeValidations: [],
      penaltyValue: formValidationService.defaultValidationResult,
    });
  const [contributionsValidationResults, setContributionsValidationResults] =
    useState<BillingContributionsValidationResult>({ bonus: [], penalty: [] });

  const [shouldShowOrderHistory, setShouldShowOrderHistory] = useState(false);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const params: BillingsOrderForwardTaxiTaxiDraftParams = {
      forwardingId: forwardingUuid!,
    };

    taxiTaxiBillingDraft.load(params, taxiTaxiBillingDraftAbort.signal);
  }, [forwardingUuid]);

  useEffect(() => {
    if (!taxiTaxiBillingDraft.data) {
      return;
    }

    onBillingDataFetchSuccess(taxiTaxiBillingDraft.data);
  }, [taxiTaxiBillingDraft.data]);

  const navigate = useNavigate();

  const documentTitle = appTranslationsHelper
    .getDocumentTitleTranslations()
    .billingsTaxiWithTaxiAdd.replace("#{routeId}", routeId);

  useDocumentTitle(documentTitle);

  useEffect(() => {
    const breadcrumbs = billingBreadcrumbsHelper.getTaxiWithTaxiAddBreadcrumbs({
      forwardingUuid: forwardingUuid!,
      routeId: routeId,
    });
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, routeId]);

  useEffect(() => {
    if (!billingSummaryData) {
      return;
    }

    const params: GpsPlanEntryRecordsParams = {
      planEntryUuid: billingSummaryData.planEntryId,
    };

    routeCompletedRoute.load(params, routeCompletedRouteAbort.signal);
  }, [billingSummaryData]);

  useEffect(() => {
    if (!routeCompletedRoute.data) {
      return;
    }

    onGpsDataFetchSuccess(routeCompletedRoute.data);
  }, [routeCompletedRoute.data]);

  useEffect(() => {
    if (!billingFormData || !shouldRetrieveRouteData) {
      return;
    }

    const mapMarkers = billingsMapMarkersFactory.createMapMarkers(
      billingFormData.billingNodes,
    );

    const routeRequests =
      billingDataRouteRequestsFactory.createBillingDataRouteRequests(
        billingFormData.billingNodes,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    routeRequests.forEach((routeRequest) => {
      const fetch = searchRoadRoutingService.search(routeRequest);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];
      const lastResponseIndex = responses.length - 1;
      let lastResponseDuration = 0;

      responses.forEach((response, index) => {
        const mapRoute = response.routes[0]
          ? billingMapRoutesFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }

        if (index == lastResponseIndex) {
          lastResponseDuration = response.routes[0].duration;
        }
      });

      const newMapRouteWaypoints: MapRoute["waypoints"] = [];

      mapRouteWaypointGroups.forEach((waypoint) => {
        newMapRouteWaypoints.push(...waypoint);
      });

      const newMapRoute: MapRoute = {
        waypoints: newMapRouteWaypoints,
        options: { color: "red" },
      };

      if (
        billingSummaryData &&
        billingSummaryData.contractDetails &&
        billingSummaryData.contractDetails.model !== BillingModel.AB
      ) {
        const lastBillingNodeIndex = billingFormData.billingNodes.length - 1;
        const secondToLastBillingNodePlannedDate =
          billingFormData.billingNodes[lastBillingNodeIndex - 1].plannedDate;

        if (secondToLastBillingNodePlannedDate) {
          const lastBillingNodePlannedDate = new Date(
            new Date(secondToLastBillingNodePlannedDate).getTime() +
              1000 * lastResponseDuration,
          );

          billingFormData.billingNodes[lastBillingNodeIndex].plannedDate =
            lastBillingNodePlannedDate;
          billingFormData.billingNodes[lastBillingNodeIndex].checkoutDate =
            lastBillingNodePlannedDate;

          setBillingData(billingFormData);
        }
      }

      setPlannedMapRoute(newMapRoute);
      setMapMarkers(mapMarkers);
      setShouldRetrieveRouteData(false);
    });
  }, [shouldRetrieveRouteData]);

  const recalculateBillingFunction = (formData: BillingFormData) => {
    const params = billingRecalculateRequestFactory.createParams(
      formData!,
      forwardingUuid!,
    );

    recalculateTaxiTaxiBilling.load(
      params,
      recalculateTaxiTaxiBillingAbort.signal,
    );
  };

  useEffect(() => {
    if (!recalculateTaxiTaxiBilling.data) {
      return;
    }

    onRecalculateSuccess(recalculateTaxiTaxiBilling.data);
  }, [recalculateTaxiTaxiBilling.data]);

  const recalculateBilling = useApiCall(recalculateBillingFunction, 50);
  const deleteContributionRecalculateBilling = useApiCall(
    recalculateBillingFunction,
    250,
  );

  const addTaxiTaxiBillingDataSuccess = () => {
    notificationService.success(translations.successNotificationText);
    navigateToListing();
  };

  const addTaxiTaxiBillingDataFailure = () => {
    notificationService.error(translations.failureNotificationText);
  };

  const onCopyFromPlannedDistance = () => {
    const newBillingNodes = billingFormData?.billingNodes.map((node) => {
      node.distance = node.plannedDistance;

      return node;
    });

    const newBillingFormData: BillingFormData = {
      ...billingFormData!,
      billingNodes: newBillingNodes!,
    };

    setBillingFormData(newBillingFormData);

    const result = billingRouteDetailsHelper.validateAllFieldsByType(
      formValidationResults,
      newBillingFormData.billingNodes,
      "distance",
    );

    nodeValidationResults(result.nodeResult!);

    if (result.isAllFormValid) recalculateBilling(newBillingFormData);
  };

  const onModalClose = () => {
    setIsSummaryConfirmationVisible(false);
  };

  const openModal = () => {
    setIsSummaryConfirmationVisible(true);
  };

  const navigateToListing = () => {
    navigate(billingRoutesHelper.getTaxiUnbilledRouteListingRoute());
  };

  const onBillingDataFetchSuccess = (
    response: BillingsOrderForwardTaxiTaxiDraft,
  ) => {
    const billingData = billingDataFactory.createBillingData(response);
    const billingSummaryData =
      billingDataFactory.createBillingSummaryData(response);

    setBillingData(billingData);
    setBillingFormData(billingData);
    setBillingSummaryData(billingSummaryData);
    setShouldRetrieveRouteData(true);
    setRouteId(String(billingSummaryData.internalOrderId));

    setFormValidationResults((current) => ({
      discountValue: current.discountValue,
      nodeValidations: billingData.billingNodes.map((x) => {
        return {
          haltingTime: formValidationService.defaultValidationResult,
          distance: formValidationService.defaultValidationResult,
          highwayCharge: formValidationService.defaultValidationResult,
          position: x.position,
        };
      }),
      penaltyValue: current.penaltyValue,
    }));

    const contributionsValidationResult: BillingContributionsValidationResult =
      {
        bonus: billingData.billingContributions.bonus.map(
          (contribution, index) => ({
            position: index,
            type: formValidationService.defaultValidationResult,
            distance: formValidationService.defaultValidationResult,
            rate: formValidationService.defaultValidationResult,
            amount: formValidationService.defaultValidationResult,
            comment: formValidationService.defaultValidationResult,
          }),
        ),
        penalty: billingData.billingContributions.penalty.map(
          (contribution, index) => ({
            position: index,
            type: formValidationService.defaultValidationResult,
            amount: formValidationService.defaultValidationResult,
            comment: formValidationService.defaultValidationResult,
          }),
        ),
      };

    setContributionsValidationResults(contributionsValidationResult);
  };

  const onGpsDataFetchSuccess = (response: GpsPlanEntryRecords) => {
    const gpsData: MapRoute = {
      waypoints: response.data.map((x) => {
        return { latitude: x.latitude, longitude: x.longitude };
      }),
      options: { color: "#3699ff" },
    };

    setCompletedMapRoute(gpsData);
  };

  const onBillingDataSave = () => {
    const areNodesValid =
      billingFormData?.billingNodes
        .map((node) => {
          return billingRouteDetailsHelper.haltingTimeValidation(
            formValidationResults,
            node.haltingTime,
            node.position,
          );
        })
        .every((x) => x) ?? false;

    const areContributionsValid = validateContributions();

    const isFormValid =
      areContributionsValid && areNodesValid && validatePenalty();

    if (!isFormValid) {
      notificationService.error(translations.failureValidationNotificationText);
      onModalClose();
      return;
    }

    if (billingFormData && billingSummaryData) {
      setIsBillingSavePending(true);

      const params = billingSaveRequestFactory.createSaveBillingParams(
        billingFormData,
        forwardingUuid!,
      );

      billingService
        .addOrderForwardTaxiTaxiBillings(
          params,
          addTaxiTaxiBillingDataAbort.signal,
        )
        .then(addTaxiTaxiBillingDataSuccess)
        .catch(addTaxiTaxiBillingDataFailure)
        .finally(() => setIsBillingSavePending(false));
    }
  };

  const validatePenalty = () => {
    const validationResult =
      billingRouteDetailsDataValidationService.validatePenalty(
        billingSummaryData?.sumOfPenalties ?? 0,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      penaltyValue: validationResult,
    }));

    return validationResult.isValid;
  };

  const validationOnBlur = (position: number, fieldName: string) => {
    type Node = keyof typeof node;

    const node = billingFormData?.billingNodes.find(
      (x) => x.position === position,
    );

    const fieldToValidate = node && node[fieldName as Node];

    const results = billingRouteDetailsHelper.validationOnBlur(
      fieldToValidate,
      formValidationResults,
      position,
      fieldName,
    );

    nodeValidationResults(results.nodeResult!);

    if (results.isAllFormValid) recalculateBilling(billingFormData!);
  };

  const nodeValidationResults = (node: NodeValidationResult[]) => {
    setFormValidationResults((curr) => ({
      ...curr,
      nodeValidations: node,
    }));
  };

  const onBillingNodeChanged = (node: BillingsNode) => {
    const nodes = billingFormData?.billingNodes.filter(
      (x) => x.position !== node.position,
    )!;

    nodes.push(node);

    const sortedArray = nodes.sort((a, b) =>
      a.position < b.position ? -1 : 1,
    );

    setBillingFormData((current) => ({
      ...current!,
      billingNodes: sortedArray,
    }));

    setShouldRetrieveRouteData(true);
  };

  const onInputBlur = () => {
    recalculateBilling(billingFormData!);
  };

  const onRecalculateSuccess = (
    response: BillingsOrderForwardTaxiTaxiRecalculate,
  ) => {
    const billingData = billingDataFactory.createBillingData(response);
    const billingSummaryData =
      billingDataFactory.createBillingSummaryData(response);

    setBillingData(billingData);
    setBillingSummaryData(billingSummaryData);
  };

  const mapRoutes: MapRoute[] = useMemo(() => {
    const finalMapRoutes: MapRoute[] = [];
    if (plannedMapRoute) {
      finalMapRoutes.push(plannedMapRoute);
    }
    if (completedMapRoute) {
      finalMapRoutes.push(completedMapRoute);
    }
    return finalMapRoutes;
  }, [plannedMapRoute, completedMapRoute]);

  const translations =
    billingsTranslationsHelper.getTaxiTaxiAddBillingsTranslations();

  const messengerChannelsAvailability =
    billingsTaxiTaxiHelper.getMessengerChannelAvailability();

  const validateContributionType = (
    type:
      | BillingsTaxiTaxiContributionBonusType
      | BillingsTaxiTaxiContributionPenaltyType
      | null,
    position: number,
    contributionType: BillingContributionType,
  ) => {
    const validationResult =
      billingsTaxiTaxiContributionsValidationService.validateType(type);

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      ...(contributionType === BillingContributionType.BONUS && {
        bonus: contributionsValidationResults.bonus.map((validation) =>
          validation.position === position
            ? {
                ...validation,
                type: validationResult,
              }
            : validation,
        ),
      }),
      ...(contributionType === BillingContributionType.PENALTY && {
        penalty: contributionsValidationResults.penalty.map((validation) =>
          validation.position === position
            ? {
                ...validation,
                type: validationResult,
              }
            : validation,
        ),
      }),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionDistance = (
    distance: number | null,
    position: number,
  ) => {
    const validationResult =
      billingsTaxiTaxiContributionsValidationService.validateDistance(distance);

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      bonus: contributionsValidationResults.bonus.map((validation) =>
        validation.position === position
          ? {
              ...validation,
              distance: validationResult,
            }
          : validation,
      ),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionRate = (rate: number | null, position: number) => {
    const validationResult =
      billingsTaxiTaxiContributionsValidationService.validateRate(rate);

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      bonus: contributionsValidationResults.bonus.map((validation, index) =>
        validation.position === position
          ? {
              ...validation,
              rate: validationResult,
            }
          : validation,
      ),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionAmount = (
    amount: number | null,
    position: number,
    contributionType: BillingContributionType,
  ) => {
    const validationResult =
      billingsTaxiTaxiContributionsValidationService.validateAmount(amount);

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      ...(contributionType === BillingContributionType.BONUS && {
        bonus: contributionsValidationResults.bonus.map((validation) =>
          validation.position === position
            ? {
                ...validation,
                amount: validationResult,
              }
            : validation,
        ),
      }),
      ...(contributionType === BillingContributionType.PENALTY && {
        penalty: contributionsValidationResults.penalty.map((validation) =>
          validation.position === position
            ? {
                ...validation,
                amount: validationResult,
              }
            : validation,
        ),
      }),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const validateContributionComment = (
    comment: string | null,
    position: number,
    contributionType: BillingContributionType,
  ) => {
    const validationResult =
      billingsTaxiTaxiContributionsValidationService.validateComment(
        comment ?? "",
      );

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      ...(contributionType === BillingContributionType.BONUS && {
        bonus: contributionsValidationResults.bonus.map((validation) =>
          validation.position === position
            ? {
                ...validation,
                comment: validationResult,
              }
            : validation,
        ),
      }),
      ...(contributionType === BillingContributionType.PENALTY && {
        penalty: contributionsValidationResults.penalty.map(
          (validation, index) =>
            validation.position === position
              ? {
                  ...validation,
                  comment: validationResult,
                }
              : validation,
        ),
      }),
    };

    setContributionsValidationResults(validationResults);

    return validationResult.isValid;
  };

  const onContributionTypeChange = (
    position: number,
    type:
      | BillingsTaxiTaxiContributionBonusType
      | BillingsTaxiTaxiContributionPenaltyType,
    contributionType: BillingContributionType,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        ...(contributionType === BillingContributionType.BONUS && {
          bonus: billingFormData!.billingContributions.bonus.map(
            (contribution, index) =>
              index === position
                ? type ===
                  BillingsTaxiTaxiContributionBonusType.BONUS_FAVORABLE_DISTANCE
                  ? ({
                      ...contribution,
                      type,
                      rate: billingSummaryData?.contractDetails?.distanceRate,
                    } as BillingsTaxiTaxiContributionBonusFavorableDistance)
                  : ({
                      ...contribution,
                      type,
                    } as BillingsTaxiTaxiContributionBonusOther)
                : contribution,
          ),
        }),
        ...(contributionType === BillingContributionType.PENALTY && {
          penalty: billingFormData!.billingContributions.penalty.map(
            (contribution, index) =>
              index === position
                ? ({
                    ...contribution,
                    type,
                  } as BillingsTaxiTaxiContributionPenalty)
                : contribution,
          ),
        }),
      },
    };

    setBillingFormData(formData);
    recalculateBilling(formData);
  };

  const onContributionDistanceChange = (
    position: number,
    distance: number | null,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        bonus: billingFormData!.billingContributions.bonus.map(
          (contribution, index) =>
            index === position
              ? ({
                  ...contribution,
                  distance,
                } as BillingsTaxiTaxiContributionBonus)
              : contribution,
        ),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionRateChange = (position: number, rate: number | null) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        bonus: billingFormData!.billingContributions.bonus.map(
          (contribution, index) =>
            index === position
              ? ({
                  ...contribution,
                  rate,
                } as BillingsTaxiTaxiContributionBonus)
              : contribution,
        ),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionAmountChange = (
    position: number,
    amount: number | null,
    contributionType: BillingContributionType,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        ...(contributionType === BillingContributionType.BONUS && {
          bonus: billingFormData!.billingContributions.bonus.map(
            (contribution, index) =>
              index === position
                ? ({
                    ...contribution,
                    amount,
                  } as BillingsTaxiTaxiContributionBonus)
                : contribution,
          ),
        }),
        ...(contributionType === BillingContributionType.PENALTY && {
          penalty: billingFormData!.billingContributions.penalty.map(
            (contribution, index) =>
              index === position
                ? ({
                    ...contribution,
                    amount,
                  } as BillingsTaxiTaxiContributionPenalty)
                : contribution,
          ),
        }),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionCommentChange = (
    position: number,
    comment: string,
    contributionType: BillingContributionType,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        ...(contributionType === BillingContributionType.BONUS && {
          bonus: billingFormData!.billingContributions.bonus.map(
            (contribution, index) =>
              index === position
                ? ({
                    ...contribution,
                    comment,
                  } as BillingsTaxiTaxiContributionBonus)
                : contribution,
          ),
        }),
        ...(contributionType === BillingContributionType.PENALTY && {
          penalty: billingFormData!.billingContributions.penalty.map(
            (contribution, index) =>
              index === position
                ? ({
                    ...contribution,
                    comment,
                  } as BillingsTaxiTaxiContributionPenalty)
                : contribution,
          ),
        }),
      },
    };

    setBillingFormData(formData);
  };

  const onContributionTypeBlur = (
    position: number,
    contributionType: BillingContributionType,
  ) => {
    const contributions = billingsTaxiTaxiHelper.getContributionsByType(
      billingFormData!.billingContributions,
      contributionType,
    );

    const type = contributions[position].type;

    if (!type) {
      deleteContribution(position, contributionType, false);
    } else {
      validateContributionType(type, position, contributionType);
    }
  };

  const onContributionDistanceBlur = (position: number) => {
    const distance = (
      billingFormData!.billingContributions.bonus[
        position
      ] as BillingsTaxiTaxiContributionBonusFavorableDistance
    ).distance;

    const isDistanceValid = validateContributionDistance(distance, position);

    if (isDistanceValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const onContributionRateBlur = (position: number) => {
    const rate = (
      billingFormData!.billingContributions.bonus[
        position
      ] as BillingsTaxiTaxiContributionBonusFavorableDistance
    ).rate;

    const isRateValid = validateContributionRate(rate, position);

    if (isRateValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const onContributionAmountBlur = (
    position: number,
    contributionType: BillingContributionType,
  ) => {
    const contributions = billingsTaxiTaxiHelper.getContributionsByType(
      billingFormData!.billingContributions,
      contributionType,
    );

    const amount = (
      contributions[position] as
        | BillingsTaxiTaxiContributionBonusOther
        | BillingsTaxiTaxiContributionPenalty
    ).amount;

    const isAmountValid = validateContributionAmount(
      amount,
      position,
      contributionType,
    );

    if (isAmountValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const onContributionCommentBlur = (
    position: number,
    contributionType: BillingContributionType,
  ) => {
    const contributions = billingsTaxiTaxiHelper.getContributionsByType(
      billingFormData!.billingContributions,
      contributionType,
    );

    const comment = contributions[position].comment;

    const isCommentValid = validateContributionComment(
      comment,
      position,
      contributionType,
    );

    if (isCommentValid) {
      recalculateBilling(billingFormData!);
    }
  };

  const validateContributions = () => {
    const contributionsValidation: BillingContributionsValidationResult = {
      bonus: billingFormData!.billingContributions.bonus.map(
        (contribution, index) => {
          switch (contribution.type) {
            case BillingsTaxiTaxiContributionBonusType.BONUS_FAVORABLE_DISTANCE: {
              return {
                position: index,
                type: billingsTaxiTaxiContributionsValidationService.validateType(
                  contribution.type,
                ),
                distance:
                  billingsTaxiTaxiContributionsValidationService.validateDistance(
                    contribution.distance,
                  ),
                rate: billingsTaxiTaxiContributionsValidationService.validateRate(
                  contribution.rate,
                ),
                amount: formValidationService.defaultValidationResult,
                comment:
                  billingsTaxiTaxiContributionsValidationService.validateComment(
                    contribution.comment,
                  ),
              };
            }
            case BillingsTaxiTaxiContributionBonusType.BONUS_OTHER: {
              return {
                position: index,
                type: billingsTaxiTaxiContributionsValidationService.validateType(
                  contribution.type,
                ),
                distance: formValidationService.defaultValidationResult,
                rate: formValidationService.defaultValidationResult,
                amount:
                  billingsTaxiTaxiContributionsValidationService.validateAmount(
                    contribution.amount,
                  ),
                comment:
                  billingsTaxiTaxiContributionsValidationService.validateComment(
                    contribution.comment,
                  ),
              };
            }
            default: {
              return {
                position: index,
                type: formValidationService.defaultValidationResult,
                distance: formValidationService.defaultValidationResult,
                rate: formValidationService.defaultValidationResult,
                amount: formValidationService.defaultValidationResult,
                comment: formValidationService.defaultValidationResult,
              };
            }
          }
        },
      ),
      penalty: billingFormData!.billingContributions.penalty.map(
        (contribution, index) => {
          return {
            position: index,
            type: billingsTaxiTaxiContributionsValidationService.validateType(
              contribution.type,
            ),
            amount:
              billingsTaxiTaxiContributionsValidationService.validateAmount(
                contribution.amount,
              ),
            comment:
              billingsTaxiTaxiContributionsValidationService.validateComment(
                contribution.comment,
              ),
          };
        },
      ),
    };

    setContributionsValidationResults(contributionsValidation);

    return (
      contributionsValidation.bonus.every(
        (validation) =>
          validation.amount.isValid &&
          validation.comment.isValid &&
          validation.distance.isValid &&
          validation.rate.isValid &&
          validation.type.isValid,
      ) &&
      contributionsValidation.penalty.every(
        (validation) =>
          validation.amount.isValid &&
          validation.comment.isValid &&
          validation.type.isValid,
      )
    );
  };

  const deleteContribution = (
    position: number,
    type: BillingContributionType,
    recalculate: boolean = true,
  ) => {
    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        ...(type === BillingContributionType.BONUS && {
          bonus: billingFormData!.billingContributions.bonus.filter(
            (contribution, index) => index !== position,
          ),
        }),
        ...(type === BillingContributionType.PENALTY && {
          penalty: billingFormData!.billingContributions.penalty.filter(
            (contribution, index) => index !== position,
          ),
        }),
      },
    };

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      ...(type === BillingContributionType.BONUS && {
        bonus: contributionsValidationResults.bonus
          .filter((validation) => validation.position !== position)
          .map((validation, index) => ({ ...validation, position: index })),
      }),
      ...(type === BillingContributionType.PENALTY && {
        penalty: contributionsValidationResults.penalty
          .filter((validation) => validation.position !== position)
          .map((validation, index) => ({ ...validation, position: index })),
      }),
    };

    setBillingFormData(formData);
    setContributionsValidationResults(validationResults);

    if (recalculate) {
      deleteContributionRecalculateBilling(formData);
    }
  };

  const addNewContribution = () => {
    const newContributionType =
      billingsTaxiTaxiHelper.getContributionTypeFromTabIndex(activeTab);

    if (!newContributionType) {
      return;
    }

    const newContribution =
      billingsTaxiTaxiHelper.getNewContribution(newContributionType);

    const formData: BillingFormData = {
      ...billingFormData!,
      billingContributions: {
        ...billingFormData!.billingContributions,
        ...(newContributionType === BillingContributionType.BONUS && {
          bonus: [
            ...billingFormData!.billingContributions.bonus,
            newContribution as BillingsTaxiTaxiContributionBonus,
          ],
        }),
        ...(newContributionType === BillingContributionType.PENALTY && {
          penalty: [
            ...billingFormData!.billingContributions.penalty,
            newContribution as BillingsTaxiTaxiContributionPenalty,
          ],
        }),
      },
    };

    const validationResults: BillingContributionsValidationResult = {
      ...contributionsValidationResults,
      ...(newContributionType === BillingContributionType.BONUS && {
        bonus: [
          ...contributionsValidationResults.bonus,
          {
            position: contributionsValidationResults.bonus.length,
            type: formValidationService.defaultValidationResult,
            distance: formValidationService.defaultValidationResult,
            rate: formValidationService.defaultValidationResult,
            amount: formValidationService.defaultValidationResult,
            comment: formValidationService.defaultValidationResult,
          },
        ],
      }),
      ...(newContributionType === BillingContributionType.PENALTY && {
        penalty: [
          ...contributionsValidationResults.penalty,
          {
            position: contributionsValidationResults.penalty.length,
            type: formValidationService.defaultValidationResult,
            amount: formValidationService.defaultValidationResult,
            comment: formValidationService.defaultValidationResult,
          },
        ],
      }),
    };

    setBillingFormData(formData);
    setContributionsValidationResults(validationResults);
  };

  const tabsData: TabsData = useMemo(() => {
    if (!billingFormData) {
      return [];
    }

    return billingDataFactory.createTabsData(
      billingFormData.billingContributions,
      isBillingDataLoading,
      deleteContribution,
      contributionsValidationResults,
      onContributionTypeChange,
      onContributionDistanceChange,
      onContributionRateChange,
      onContributionAmountChange,
      onContributionCommentChange,
      onContributionTypeBlur,
      onContributionDistanceBlur,
      onContributionRateBlur,
      onContributionAmountBlur,
      onContributionCommentBlur,
    );
  }, [billingFormData, contributionsValidationResults, selectedAppLanguage]);

  const tabMaxContributions = useMemo(
    () => billingsTaxiTaxiHelper.getMaxContributionsFromTabIndex(activeTab),
    [activeTab],
  );

  const onOrderHistoryButtonClick = () => {
    if (shouldShowOrderHistory) {
      setShouldShowOrderHistory(false);
      return;
    }

    setShouldShowOrderHistory(true);
  };

  const billingsTaxiTaxiToolsProps = {
    shouldShowOrderHistory,
    onOrderHistoryButtonClick,
  };

  return (
    <>
      <div className="billings">
        <HeadingComponent
          title={translations.header.headingText.replace(
            "#{routeId}",
            billingSummaryData?.internalOrderId.toString() ?? "",
          )}
        />
        <Row>
          <Column lg={8}>
            <Row>
              <Column withPaddings>
                <div className="billings_map_wrapper">
                  <MapComponent
                    markers={mapMarkers}
                    autoFit
                    autoFitOnUpdate
                    routes={mapRoutes}
                  />
                  <BillingsToolsComponent {...billingsTaxiTaxiToolsProps} />
                </div>
              </Column>
              <Column withPaddings>
                <ContractDetailsComponent
                  isLoading={isBillingDataLoading}
                  contractDetails={billingSummaryData?.contractDetails ?? null}
                />
              </Column>
              <Column withPaddings>
                <Row>
                  <Column lg={12}>
                    <BillingsRouteDetailsFormComponent
                      billingNodes={billingFormData?.billingNodes}
                      formValidationResults={
                        formValidationResults.nodeValidations
                      }
                      onBillingNodeChanged={onBillingNodeChanged}
                      onInputBlur={onInputBlur}
                      onCopyFromPlannedDistance={onCopyFromPlannedDistance}
                      validationOnBlur={validationOnBlur}
                    />
                  </Column>
                </Row>
              </Column>
              <Column withPaddings>
                <BillingRouteDetailsComponent
                  billingSummaryData={billingSummaryData}
                />
              </Column>
              {!!billingData && (
                <Column withPaddings>
                  <TabsComponent
                    data={tabsData}
                    onActiveTabChange={setActiveTab}
                  />
                  <div className="billings_contributions_add_button">
                    <ButtonComponent
                      onClick={addNewContribution}
                      type="success"
                      title={translations.contributionAddButtonTitle}
                      isDisabled={
                        tabsData[activeTab].totalTableRows >=
                        tabMaxContributions
                      }
                    >
                      {translations.contributionAddButtonLabel}
                    </ButtonComponent>
                  </div>
                </Column>
              )}
              <Column withPaddings>
                <CardComponent
                  classNames={{ root: "billing_details" }}
                  header={{ title: translations.billingSummary.summaryLabel }}
                >
                  <BillingDetailsComponent
                    billingData={billingFormData}
                    billingSummaryData={billingSummaryData}
                  />
                </CardComponent>
              </Column>
              <Column>
                <ButtonComponent type="primary" onClick={openModal}>
                  {translations.submitLabel}
                </ButtonComponent>
              </Column>
              <Column withPaddings>
                <RelatedBillingsCargoTaxiComponent
                  forwardingId={billingSummaryData?.forwardingId!}
                />
              </Column>
              <Column withPaddings>
                <RelatedBillingsTaxiDriverComponent
                  planEntryId={billingSummaryData?.planEntryId}
                  forwardingId={billingSummaryData?.forwardingId!}
                />
              </Column>
              <Column withPaddings>
                <RelatedBillingsTaxiTaxiComponent
                  forwardingId={billingSummaryData?.forwardingId!}
                />
              </Column>
            </Row>
          </Column>
          <Column lg={4} withPaddings>
            <CardComponent
              classNames={{
                root: "billings_history_messenger_container",
                content: "h-100",
              }}
            >
              {billingSummaryData?.planEntryId &&
                (shouldShowOrderHistory ? (
                  <OrderDetailsHistoryComponent
                    orderUuid={billingSummaryData.orderId}
                    refetchFlag={false}
                  />
                ) : (
                  <MessengerComponent
                    channelsAvailability={messengerChannelsAvailability}
                    orderUuid={billingSummaryData.orderId}
                    planEntryUuid={billingSummaryData.planEntryId}
                  />
                ))}
            </CardComponent>
          </Column>
        </Row>
        <BillingsSummaryConfirmationComponent
          isVisible={isSummaryConfirmationVisible}
          onSubmit={onBillingDataSave}
          onClose={onModalClose}
          billingData={billingData!}
          billingSummaryData={billingSummaryData!}
          isLoading={isBillingSavePending}
        />
      </div>
    </>
  );
};

export default BillingsTaxiTaxiAddComponent;
